import { cloneDeep } from 'lodash-es'
import mitt, { type EventType } from 'mitt'
import type {
  KeyEventInfoHandler,
  KeyEventParams,
  MouseEventInfoHandler,
  MouseEventParams,
} from '@/plugins/cad/module/core/common/event'
import type { BaseTool } from '@/plugins/cad/module/core/tool/BaseTool'
import { KeyCodes } from '@/shared/constants'
import type { Detector } from './Detector'
import type { FrameSelect } from './FrameSelect'

/** 检查事件目标是否为可编辑元素（阻止在输入框中误触选择） */
function isEditableEventTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  const tagName = target.tagName
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName)
}

/** 选择模式 */
export enum SelectMode {
  /** 加选 */
  add = 'add',
  /** 减选 */
  sub = 'sub',
  /** 替换 */
  replace = 'replace',
}

/** 选择选项 */
export interface SelectOptions {
  /** 选择模式 */
  mode?: SelectMode
  /** 是否启用框选功能，默认为 true */
  enableFrameSelect?: boolean
  /** 是否启用鼠标点击选择，默认为 true */
  enableMouseSelect?: boolean
}

/**
 * 选择器事件处理钩子集合，工具可实现此接口响应选择变更。
 *
 * @typeParam T - 被选择的实体类型
 */
export interface SelectorEventHandler<T> {
  /** 选择集合发生变化的回调 */
  onSelectorChanged?: (entities: T[]) => void
}

/** 选择器基类 */
export abstract class Selector<T extends { id: string }>
  implements MouseEventInfoHandler, KeyEventInfoHandler
{
  protected abstract detector: Detector<T>

  /** 当前选中的实体集合 */
  protected selectedEntities: Set<T> = new Set()

  protected readonly emitter = mitt()

  /** 当前按下的键码集合 */
  protected readonly pressedKeyCodeSet: Set<number> = new Set()

  /** 当前宿主工具实例 */
  tool: BaseTool | undefined

  private _frameSelect: FrameSelect<T> | undefined

  protected defaultSetting: SelectOptions = {
    enableFrameSelect: true,
    enableMouseSelect: true,
  }

  protected setting: SelectOptions = {
    enableFrameSelect: true,
    enableMouseSelect: true,
  }

  /**
   * 选择对象。
   *
   * @param items - 待选中的单个实体或实体数组
   * @param options - 选择选项，控制选择模式与是否启用框选
   */
  select(items: T | T[], options?: SelectOptions) {
    this.handleSelect(items, options)
    this.onSelectionChanged(this.getSelected())
  }

  /**
   * 取消选中指定对象。
   *
   * @param items - 待取消选中的单个实体或实体数组
   */
  deselect(items: T | T[]) {
    this.handleSelect(items, { mode: SelectMode.sub })
    this.onSelectionChanged(this.getSelected())
  }

  protected selectItem(item: T): void {
    if (!item) {
      return
    }
    this.selectedEntities.add(item)
    this.onItemSelected(item)
  }

  protected deselectItem(item: T): void {
    if (!item) {
      return
    }
    this.selectedEntities.delete(item)
    this.onItemDeselected(item)
  }

  /** 清空当前全部选中实体 */
  clearSelection(): void {
    const entities = Array.from(this.selectedEntities)
    this.selectedEntities.clear()
    this.onSelectionCleared(entities)
    this.onSelectionChanged([])
  }

  /**
   * 获取当前全部选中实体。
   *
   * @returns 选中实体数组；未选中任何实体时返回空数组
   */
  getSelected(): T[] {
    return Array.from(this.selectedEntities)
  }

  /**
   * 获取当前选中实体数量。
   *
   * @returns 选中实体个数，≥0
   */
  getSelectedCount(): number {
    return this.selectedEntities.size
  }

  /**
   * 判断指定实体是否已被选中。
   *
   * @param entity - 待检查的实体
   * @returns 已选中返回 true，否则返回 false
   */
  isSelected(entity: T): boolean {
    return this.selectedEntities.has(entity)
  }

  /**
   * 判断当前是否存在任意选中实体。
   *
   * @returns 存在选中返回 true，否则返回 false
   */
  hasSelection(): boolean {
    return this.selectedEntities.size > 0
  }

  protected handleSelect(items: T | T[], options: SelectOptions = {}): void {
    const mode = this.getSelectMode(options)
    const itemsToSelect = Array.isArray(items) ? items : [items]

    switch (mode) {
      case SelectMode.add: {
        for (const item of itemsToSelect) {
          if (!item || !item.id) {
            continue
          }
          this.selectItem(item)
        }
        break
      }
      case SelectMode.sub: {
        for (const item of itemsToSelect) {
          if (!item || !item.id) {
            continue
          }
          this.deselectItem(item)
        }
        break
      }
      default:
        this.clearSelection()
        for (const item of itemsToSelect) {
          if (!item || !item.id) {
            continue
          }
          this.selectItem(item)
        }
        break
    }
  }

  protected getSelectMode(options: SelectOptions): SelectMode {
    if (this.pressedKeyCodeSet.has(KeyCodes.SHIFT)) {
      return SelectMode.sub
    } else if (options.mode) {
      return options.mode
    } else if (this.pressedKeyCodeSet.has(KeyCodes.CTRL)) {
      return SelectMode.add
    } else {
      return SelectMode.replace
    }
  }

  protected onItemSelected(_item: T): void {}
  protected onItemDeselected(_item: T): void {}
  protected onSelectionCleared(_cleared: T[]): void {}

  protected onSelectionChanged(items: T[]): void {
    const tool = this.tool as SelectorEventHandler<T> | undefined
    tool?.onSelectorChanged?.(items)
    this.emit('onSelectorChanged', items)
  }

  /************ 事件管理 ************/
  /**
   * 订阅选择器事件。
   *
   * @param type - 事件名称
   * @param handler - 事件触发时调用的回调
   */
  on(type: EventType, handler: (...args: any[]) => void) {
    this.emitter.on(type, handler)
  }

  /**
   * 触发选择器事件。
   *
   * @param type - 事件名称
   * @param event - 透传给订阅者的事件数据
   */
  emit(type: EventType, event?: any) {
    this.emitter.emit(type, event)
  }

  /**
   * 取消订阅选择器事件。
   *
   * @param type - 事件名称
   * @param handler - 需要移除的回调，省略时清除该事件的所有订阅
   */
  off(type: EventType, handler?: (...args: any[]) => void) {
    this.emitter.off(type, handler)
  }

  /************ 鼠标键盘事件处理 ************/
  /**
   * 鼠标按下回调，左键点击时基于探测结果执行选择。
   *
   * @param params - 鼠标事件参数
   */
  onMouseDown(params: MouseEventParams) {
    if (this.frameSelect.isFraming) {
      return
    }
    if (!this.getEnableMouseSelect()) {
      return
    }
    if (params.event.button === 0) {
      const detected = this.detector.getOneEntity()
      this.select(detected || [], this.setting)
    }
  }

  /**
   * 鼠标移动回调，驱动框选状态并同步探测开关。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    if (this.getEnableFrameSelect()) {
      this.frameSelect.onMouseMove(params)
    }
    this.detector.config({ enableDetect: !this.frameSelect.isFraming })
  }

  /**
   * 键盘按下回调，记录修饰键状态并处理 ESC 取消。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (isEditableEventTarget(params.event.target)) {
      return
    }
    const keyCode = params.event.keyCode
    if (keyCode === KeyCodes.ESC) {
      this.frameSelect.cancel()
      this.clearSelection()
    }
    this.pressedKeyCodeSet.add(keyCode)
  }

  /**
   * 键盘抬起回调，清除对应按键状态。
   *
   * @param params - 键盘事件参数
   */
  onKeyUp(params: KeyEventParams) {
    if (isEditableEventTarget(params.event.target)) {
      return
    }
    const keyCode = params.event.keyCode
    this.pressedKeyCodeSet.delete(keyCode)
  }

  /************ 注入工具 ************/
  /**
   * 注入宿主工具并启用探测高亮。
   *
   * @param tool - 当前使用该选择器的工具
   */
  injectTool(tool: BaseTool): void {
    this.tool = tool
    tool.inject(this.detector)
    this.detector.config({ enableHighlight: true })
  }

  /** 解除宿主工具的引用并关闭探测高亮 */
  ejectTool(): void {
    this.detector.config({ enableHighlight: false })
    this.tool?.eject(this.detector)
    this.tool = undefined
    this.pressedKeyCodeSet.clear()
    this.frameSelect.cancel()
  }

  /************ 框选 ************/
  get frameSelect(): FrameSelect<T> {
    if (!this._frameSelect) {
      this._frameSelect = this.getFrameSelect()
    }
    return this._frameSelect
  }

  protected abstract getFrameSelect(): FrameSelect<T>

  /************ 配置 ************/
  /**
   * 合并更新选择器配置。
   *
   * @param option - 需要覆盖的配置字段
   */
  config(option: Partial<SelectOptions>) {
    Object.assign(this.setting, option)
    this.configCallback()
  }

  /** 将配置重置为默认值 */
  resetConfig() {
    this.setting = cloneDeep(this.defaultSetting)
    this.configCallback()
  }

  /**
   * 设置默认配置，同时将当前配置重置为默认值。
   *
   * @param option - 新的默认配置
   */
  setDefaultConfig(option: SelectOptions) {
    Object.assign(this.defaultSetting, option)
    this.resetConfig()
  }

  /** 配置变更后触发的一致性处理，如禁用框选时取消当前框选 */
  configCallback() {
    if (!this.getEnableFrameSelect()) {
      this.frameSelect.cancel()
    }
  }

  /**
   * 获取是否启用框选。
   *
   * @returns 启用返回 true，否则返回 false 或 undefined
   */
  getEnableFrameSelect() {
    return this.setting.enableFrameSelect
  }

  /**
   * 获取是否启用鼠标点击选择。
   *
   * @returns 启用返回 true，否则返回 false 或 undefined
   */
  getEnableMouseSelect() {
    return this.setting.enableMouseSelect
  }
}
